import { EmptyValue } from '@/components/common/EmptyValue';
import { usePermissions } from '@/features/auth/hooks/usePermissions';
import { RowActionButton } from './RowActionButton';
import { RowActionGroup } from './RowActionGroup';
import { RowMoreActionsMenu } from './RowMoreActionsMenu';
import type { RowAction } from './rowAction.types';

const MAX_INLINE_ACTION_COUNT = 2;

interface RowActionListProps {
  actions: readonly RowAction[];
  moreActionsAriaLabel: string;
  shouldShowAllActions?: boolean | undefined;
}

function renderActionButton(action: RowAction) {
  return (
    <RowActionButton
      key={action.key}
      label={action.label}
      ariaLabel={action.ariaLabel}
      onClick={action.onSelect}
      tone={action.tone ?? 'default'}
      isDisabled={action.isDisabled === true}
    />
  );
}

export function RowActionList({
  actions,
  moreActionsAriaLabel,
  shouldShowAllActions = false,
}: RowActionListProps) {
  const { can } = usePermissions();
  const allowed = actions.filter((action) => can(action.requires));
  const [mainAction, ...otherActions] = allowed;
  if (mainAction === undefined) return <EmptyValue />;

  if (shouldShowAllActions || allowed.length <= MAX_INLINE_ACTION_COUNT) {
    return <RowActionGroup>{allowed.map(renderActionButton)}</RowActionGroup>;
  }

  return (
    <RowActionGroup>
      {renderActionButton(mainAction)}
      <RowMoreActionsMenu actions={otherActions} ariaLabel={moreActionsAriaLabel} />
    </RowActionGroup>
  );
}
